import {Injectable} from '@angular/core';
import {map, Observable, tap} from 'rxjs';
import {DataService} from './data.service';
import {
  ApiProcessEnvelope,
  ApiResponseModel,
  ApPayoutReadinessResponse,
  ConnectTransferResponse,
  CreateApTransferRequest,
  GetApPayoutReadinessRequest,
  ReverseApTransferRequest,
  ReverseApTransferResponse
} from '../DataModels/stripe-connect-ap.models';

@Injectable({providedIn: 'root'})
export class StripeConnectApService {
  constructor(private tableDataService: DataService) {
  }

  getReadiness(payload: GetApPayoutReadinessRequest): Observable<ApPayoutReadinessResponse> {
    return this.tableDataService
      .post('stripe/rails/ap/push/readiness', payload)
      .pipe(
        this.logStripeReturn('POST', '/api/stripe/rails/ap/push/readiness'),
        map((r: any) => {
          const raw = r?.data?.[0]?.result ?? r?.Data?.[0]?.Result;
          return this.normalizeReadiness(raw);
        })
      );
  }

  createTransfer(payload: CreateApTransferRequest): Observable<ConnectTransferResponse> {
    return this.tableDataService
      .post('stripe/rails/ap/transfer', payload)
      .pipe(
        this.logStripeReturn('POST', '/api/stripe/rails/ap/transfer'),
        map((r: any) => {
          const raw = r?.data?.[0]?.result ?? r?.Data?.[0]?.Result ?? r?.data?.[0] ?? r?.Data?.[0] ?? r;
          return this.normalizeTransfer(raw);
        })
      );
  }

  reverseTransfer(payload: ReverseApTransferRequest): Observable<ReverseApTransferResponse> {
    return this.tableDataService
      .post('stripe/rails/ap/transfer/reverse', payload)
      .pipe(
        this.logStripeReturn('POST', '/api/stripe/rails/ap/transfer/reverse'),
        map((r: any) => {
          const raw = r?.data?.[0]?.result ?? r?.Data?.[0]?.Result ?? r?.data?.[0] ?? r?.Data?.[0] ?? r;
          return this.normalizeReversal(raw);
        })
      );
  }

  // AP offboarding (Connect delete)
  deleteConnect(payload: { appEntityId: string; connectedAccountId?: string }): Observable<boolean> {
    return this.tableDataService
      .post('stripe/rails/ap/push/delete-connect', payload)
      .pipe(
        this.logStripeReturn('POST', '/api/stripe/rails/ap/push/delete-connect'),
        map((r: any) => Boolean(r?.success || r?.Success || r?.data || r?.Data))
      );
  }

  // AP balances (Connect account balance)
  getBalance(payload: { appEntityId: string; connectedAccountId?: string; currency?: string }): Observable<any> {
    return this.tableDataService
      .post('stripe/rails/ap/push/balance', payload)
      .pipe(
        this.logStripeReturn('POST', '/api/stripe/rails/ap/push/balance'),
        map((r: any) => (r?.data?.[0]?.result ?? r?.Data?.[0]?.Result ?? r))
      );
  }

  // ----- Normalizers: map backend PascalCase to our camelCase interfaces -----
  private normalizeReadiness(raw: any): ApPayoutReadinessResponse {
    if (!raw || typeof raw !== 'object') {
      return {
        hasConnectedAccount: false,
        payoutsEnabled: false,
        detailsSubmitted: false,
        hasNoPastDueRequirements: false,
        isPayoutReady: false,
        requirementsCurrentlyDue: [],
        requirementsPastDue: [],
        notReadyReasons: [],
      };
    }
    // Accept either camelCase or PascalCase
    return {
      connectedAccountId: raw.connectedAccountId ?? raw.ConnectedAccountId,
      hasConnectedAccount: Boolean(raw.hasConnectedAccount ?? raw.HasConnectedAccount),
      payoutsEnabled: Boolean(raw.payoutsEnabled ?? raw.PayoutsEnabled),
      detailsSubmitted: Boolean(raw.detailsSubmitted ?? raw.DetailsSubmitted),
      hasNoPastDueRequirements: Boolean(raw.hasNoPastDueRequirements ?? raw.HasNoPastDueRequirements),
      isPayoutReady: Boolean(raw.isPayoutReady ?? raw.IsPayoutReady),
      onboardingStatus: raw.onboardingStatus ?? raw.OnboardingStatus,
      onboardingUrl: raw.onboardingUrl ?? raw.OnboardingUrl,
      requirementsCurrentlyDue: raw.requirementsCurrentlyDue ?? raw.RequirementsCurrentlyDue ?? [],
      requirementsPastDue: raw.requirementsPastDue ?? raw.RequirementsPastDue ?? [],
      notReadyReasons: raw.notReadyReasons ?? raw.NotReadyReasons ?? [],
      message: raw.message ?? raw.Message,
    } as ApPayoutReadinessResponse;
  }

  private normalizeReversal(raw: any): ReverseApTransferResponse {
    if (!raw || typeof raw !== 'object') {
      return {reversed: false, blocked: false};
    }
    return {
      transferId: raw.transferId ?? raw.TransferId,
      reversalId: raw.reversalId ?? raw.ReversalId,
      reversed: Boolean(raw.reversed ?? raw.Reversed ?? false),
      blocked: Boolean(raw.blocked ?? raw.Blocked ?? false),
      status: (raw.status ?? raw.Status ?? '').toString(),
      message: raw.message ?? raw.Message,
    };
  }

  private normalizeTransfer(raw: any): ConnectTransferResponse {
    if (!raw || typeof raw !== 'object') {
      return {status: '', amount: 0, isPayoutReady: false};
    }
    return {
      transferId: raw.transferId ?? raw.TransferId,
      status: (raw.status ?? raw.Status ?? '').toString(),
      amount: Number(raw.amount ?? raw.Amount ?? 0),
      currency: raw.currency ?? raw.Currency,
      connectedAccountId: raw.connectedAccountId ?? raw.ConnectedAccountId,
      isPayoutReady: Boolean(raw.isPayoutReady ?? raw.IsPayoutReady ?? true),
      message: raw.message ?? raw.Message,
    };
  }

  // --- Logging helper: consistently log all Stripe Connect AP POST returns (success and error) ---
  private logStripeReturn(method: 'POST' | 'GET' | 'DELETE' | 'PATCH', url: string) {
    return tap({
      next: (resp) => {
        try {
          console.log('[Stripe Connect AP][Return]', {method, url, response: resp});
        } catch {
          // no-op
        }
      },
      error: (err) => {
        try {
          console.log('[Stripe Connect AP][Error]', {method, url, error: err});
        } catch {
          // no-op
        }
      }
    });
  }
}
